// modifyMergeBranch — VQE-Edit edit-action dispatcher for MERGE branch edits.
// Delegates to SR-Edit's MERGE-branch-specific structural ops:
//   - modify-merge-branch-condition       (replace AND condition text)
//   - modify-merge-update-assignment      (replace nested SET value)
//   - add-merge-update-assignment         (append nested SET assignment)
//   - remove-merge-update-assignment      (remove nested SET assignment)
//
// These are distinct from the top-level update-assignment ops, which target
// `QueryStructure.updateAssignments`. The branch-aware ops target
// `MergeBranch.updateAssignments` of the picked branch.
//
// PRD: Ralph/VisualQueryEditorEdit/PRD.md § Slice: Edit — DML edits.

import type { MergeBranchKind, QueryStructure } from '../../../../sql-resolver/types';
import { commitEdit, type CommitEditCtx, type CommitOutcome } from '../commitEdit';

export type MergeBranchSelector = {
  readonly branchKind: MergeBranchKind;
  readonly branchIndex: number;
};

export type MergeBranchValidationOptions = {
  /** Optional structure used for branch / assignment bounds validation. */
  readonly structure?: QueryStructure;
};

function pickBranch(
  selector: MergeBranchSelector,
  structure: QueryStructure,
): { hasBranch: boolean; assignmentCount: number } {
  const list = selector.branchKind === 'matched'
    ? structure.mergeWhenMatched
    : structure.mergeWhenNotMatched;
  if (!list || selector.branchIndex < 0 || selector.branchIndex >= list.length) {
    return { hasBranch: false, assignmentCount: 0 };
  }
  return {
    hasBranch: true,
    assignmentCount: list[selector.branchIndex].updateAssignments?.length ?? 0,
  };
}

function assertBranchExists(
  selector: MergeBranchSelector,
  options: MergeBranchValidationOptions,
  label: string,
): void {
  if (!options.structure) return;
  const { hasBranch } = pickBranch(selector, options.structure);
  if (!hasBranch) {
    throw new Error(
      `${label}: branch ${selector.branchKind}#${selector.branchIndex} is out of range`,
    );
  }
}

function assertAssignmentInRange(
  selector: MergeBranchSelector,
  assignmentIndex: number,
  options: MergeBranchValidationOptions,
  label: string,
): void {
  if (!options.structure) return;
  const { hasBranch, assignmentCount } = pickBranch(selector, options.structure);
  if (!hasBranch) {
    throw new Error(
      `${label}: branch ${selector.branchKind}#${selector.branchIndex} is out of range`,
    );
  }
  if (assignmentCount === 0) {
    throw new Error(`${label}: branch SET list is empty`);
  }
  if (assignmentIndex >= assignmentCount) {
    throw new Error(
      `${label}: assignmentIndex ${assignmentIndex} out of range (branch SET list has ${assignmentCount} item(s))`,
    );
  }
}

export async function modifyMergeBranchCondition(
  selector: MergeBranchSelector,
  newCondition: string,
  ctx: CommitEditCtx,
  options: MergeBranchValidationOptions = {},
): Promise<void> {
  if (!Number.isInteger(selector.branchIndex) || selector.branchIndex < 0) {
    throw new Error(
      `modifyMergeBranchCondition: branchIndex must be a non-negative integer (got ${selector.branchIndex})`,
    );
  }
  const v = newCondition.trim();
  if (v.length === 0) {
    throw new Error('modifyMergeBranchCondition: newCondition must not be empty');
  }
  assertBranchExists(selector, options, 'modifyMergeBranchCondition');
  await commitEdit({
    op: 'modify-merge-branch-condition',
    branchKind: selector.branchKind,
    branchIndex: selector.branchIndex,
    newCondition: v,
  }, ctx);
}

export async function modifyMergeUpdateAssignment(
  selector: MergeBranchSelector,
  assignmentIndex: number,
  newValueText: string,
  ctx: CommitEditCtx,
  options: MergeBranchValidationOptions = {},
): Promise<void> {
  if (!Number.isInteger(assignmentIndex) || assignmentIndex < 0) {
    throw new Error(
      `modifyMergeUpdateAssignment: assignmentIndex must be a non-negative integer (got ${assignmentIndex})`,
    );
  }
  const v = newValueText.trim();
  if (v.length === 0) {
    throw new Error('modifyMergeUpdateAssignment: newValueText must not be empty');
  }
  assertAssignmentInRange(selector, assignmentIndex, options, 'modifyMergeUpdateAssignment');
  await commitEdit({
    op: 'modify-merge-update-assignment',
    branchKind: selector.branchKind,
    branchIndex: selector.branchIndex,
    assignmentIndex,
    newValueText: v,
  }, ctx);
}

export async function addMergeUpdateAssignment(
  selector: MergeBranchSelector,
  target: string,
  valueText: string,
  ctx: CommitEditCtx,
  options: MergeBranchValidationOptions = {},
): Promise<CommitOutcome> {
  const t = target.trim();
  const v = valueText.trim();
  if (t.length === 0) {
    throw new Error('addMergeUpdateAssignment: target must not be empty');
  }
  if (v.length === 0) {
    throw new Error('addMergeUpdateAssignment: valueText must not be empty');
  }
  assertBranchExists(selector, options, 'addMergeUpdateAssignment');
  return commitEdit({
    op: 'add-merge-update-assignment',
    branchKind: selector.branchKind,
    branchIndex: selector.branchIndex,
    target: t,
    valueText: v,
  }, ctx);
}

export async function removeMergeUpdateAssignment(
  selector: MergeBranchSelector,
  assignmentIndex: number,
  ctx: CommitEditCtx,
  options: MergeBranchValidationOptions = {},
): Promise<void> {
  if (!Number.isInteger(assignmentIndex) || assignmentIndex < 0) {
    throw new Error(
      `removeMergeUpdateAssignment: assignmentIndex must be a non-negative integer (got ${assignmentIndex})`,
    );
  }
  assertAssignmentInRange(selector, assignmentIndex, options, 'removeMergeUpdateAssignment');
  await commitEdit({
    op: 'remove-merge-update-assignment',
    branchKind: selector.branchKind,
    branchIndex: selector.branchIndex,
    assignmentIndex,
  }, ctx);
}
